import { apiFetch } from '@/lib/api'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { ChevronLeft, RefreshCw, AlertTriangle } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Skeleton } from '@/components/ui/Skeleton'
import { MilestoneProgress } from '@/components/logs/MilestoneProgress'
import { ProvisionLogViewer } from '@/components/logs/ProvisionLogViewer'
import { useRegistryVersions } from '@/hooks/useRegistryVersions'
import { useProvisionLogsByJob } from '@/hooks/useProvisionLogs'
import { UPDATE_MILESTONES } from '@/types'
import type { TTenant } from '@/types'

const updateSchema = z.object({
  web_app_version: z.string().optional(),
  openclaw_version: z.string().optional(),
  update_web_app: z.boolean(),
  update_openclaw: z.boolean(),
})
type UpdateFormData = z.infer<typeof updateSchema>

interface UpdateWizardProps { isOpen: boolean; onClose: () => void; tenant: TTenant; onComplete?: () => void }

const selectStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', backgroundColor: 'var(--bg-tertiary)',
  border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)',
  fontSize: '13px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', outline: 'none',
}

export function UpdateWizard({ isOpen, onClose, tenant, onComplete }: UpdateWizardProps) {
  type WizardStep = 'versions' | 'env_vars' | 'progress'
  const [step, setStep] = useState<WizardStep>('versions')
  const [jobId, setJobId] = useState<string | null>(null)
  const [provisionStartedAt, setProvisionStartedAt] = useState<string | null>(null)
  const [provisionCompletedAt, setProvisionCompletedAt] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [newEnvVars, setNewEnvVars] = useState<string[]>([])

  const { data: versions, isLoading: versionsLoading } = useRegistryVersions(isOpen)

  // Auto-detect PocketBase/Caddy upgrades
  const latestPb = versions?.pocketbase?.[0] ?? null
  const latestCaddy = versions?.caddy?.[0] ?? null
  const currentPb = tenant.pocketbase_tag || '0.36.4-b3'
  const currentCaddy = tenant.caddy_tag || 'cad-b2'
  const pbUpgrade = latestPb && latestPb !== currentPb ? latestPb : null
  const caddyUpgrade = latestCaddy && latestCaddy !== currentCaddy ? latestCaddy : null

  // Get logs via job ID → provision → logs (clean chain, no guessing)
  const { data: logs = [], provisionId } = useProvisionLogsByJob(jobId)

  const form = useForm<UpdateFormData>({
    resolver: zodResolver(updateSchema),
    defaultValues: { web_app_version: tenant.web_app_version || '', openclaw_version: tenant.openclaw_version || '', update_web_app: true, update_openclaw: true },
  })
  const watchUpdateWebApp = form.watch('update_web_app')
  const watchUpdateOpenclaw = form.watch('update_openclaw')

  const isComplete = useMemo(() => logs.some(l => l.milestone === 'update_complete' && l.milestone_ended), [logs])
  const isFailed = useMemo(() => logs.some(l => l.level === 'error' && l.message?.toLowerCase().includes('failed')), [logs])

  useEffect(() => {
    if (isComplete && !provisionCompletedAt) {
      setProvisionCompletedAt(logs[logs.length - 1]?.timestamp ?? new Date().toISOString())
      toast.success('Update completed successfully'); onComplete?.()
    }
  }, [isComplete, provisionCompletedAt, logs, onComplete])

  // Only reset when the modal opens — NOT when tenant data refreshes mid-update
  const prevOpen = useRef(isOpen)
  useEffect(() => {
    if (isOpen && !prevOpen.current) {
      setStep('versions'); setJobId(null); setProvisionStartedAt(null); setProvisionCompletedAt(null); setIsSubmitting(false); setNewEnvVars([])
      form.reset({ web_app_version: tenant.web_app_version || '', openclaw_version: tenant.openclaw_version || '', update_web_app: true, update_openclaw: true })
    }
    prevOpen.current = isOpen
  }, [isOpen, tenant, form])

  const handleSubmit = useCallback(async () => {
    if (!(await form.trigger())) return
    const values = form.getValues()
    if (!values.update_web_app && !values.update_openclaw) { toast.error('Select at least one component to update'); return }
    setIsSubmitting(true)
    try {
      const body: Record<string, string> = {}
      if (values.update_web_app && values.web_app_version) body.web_app_version = values.web_app_version
      if (values.update_openclaw && values.openclaw_version) body.openclaw_version = values.openclaw_version
      if (pbUpgrade) body.pocketbase_tag = pbUpgrade
      if (caddyUpgrade) body.caddy_tag = caddyUpgrade
      const res = await apiFetch(`/api/tenants/${tenant.id}/update`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Update failed' }))
        if (err.new_env_vars && Array.isArray(err.new_env_vars) && err.new_env_vars.length > 0) { setNewEnvVars(err.new_env_vars); setStep('env_vars'); return }
        throw new Error(err.message ?? `Update failed: ${res.status}`)
      }
      const data = await res.json()
      setJobId(data.job_id)
      setProvisionStartedAt(new Date().toISOString())
      setStep('progress')
    } catch (err) { toast.error(err instanceof Error ? err.message : 'Update failed') } finally { setIsSubmitting(false) }
  }, [form, tenant.id, pbUpgrade, caddyUpgrade])

  const handleEnvVarsContinue = useCallback(async () => {
    setIsSubmitting(true)
    try {
      const values = form.getValues()
      const body: Record<string, unknown> = { acknowledge_new_vars: true }
      if (values.update_web_app && values.web_app_version) body.web_app_version = values.web_app_version
      if (values.update_openclaw && values.openclaw_version) body.openclaw_version = values.openclaw_version
      if (pbUpgrade) body.pocketbase_tag = pbUpgrade
      if (caddyUpgrade) body.caddy_tag = caddyUpgrade
      const res = await apiFetch(`/api/tenants/${tenant.id}/update`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      if (!res.ok) { const err = await res.json().catch(() => ({ message: 'Update failed' })); throw new Error(err.message ?? `Update failed: ${res.status}`) }
      const data = await res.json()
      setJobId(data.job_id)
      setProvisionStartedAt(new Date().toISOString())
      setStep('progress')
    } catch (err) { toast.error(err instanceof Error ? err.message : 'Update failed') } finally { setIsSubmitting(false) }
  }, [form, tenant.id, pbUpgrade, caddyUpgrade])

  const stepLabel = step === 'versions' ? 'Select Versions' : step === 'env_vars' ? 'New Environment Variables' : 'Update Progress'
  const canClose = step !== 'progress'

  return (
    <Modal isOpen={isOpen} onClose={canClose ? onClose : () => {}} title={`Update ${tenant.name}`} subtitle={stepLabel} maxWidth="640px" footer={
      step === 'progress' ? <Button variant="primary" onClick={onClose} disabled={!isComplete && !isFailed}>{isComplete ? 'Done' : isFailed ? 'Close' : 'Updating...'}</Button>
      : step === 'env_vars' ? <><Button variant="ghost" icon={<ChevronLeft size={14} />} onClick={() => setStep('versions')}>Back</Button><Button variant="primary" onClick={handleEnvVarsContinue} disabled={isSubmitting}>{isSubmitting ? 'Starting...' : 'Continue with Update'}</Button></>
      : <Button variant="primary" icon={<RefreshCw size={14} />} onClick={handleSubmit} disabled={isSubmitting || (!watchUpdateWebApp && !watchUpdateOpenclaw)}>{isSubmitting ? 'Starting...' : 'Start Update'}</Button>
    }>
      {step === 'versions' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ padding: '12px 16px', backgroundColor: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Current Versions</span>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>Web App: <strong>{tenant.web_app_version || 'unknown'}</strong></span>
              <span style={{ fontSize: '12px', fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>OpenClaw: <strong>{tenant.openclaw_version || 'unknown'}</strong></span>
              <span style={{ fontSize: '12px', fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>PocketBase: <strong>{currentPb}</strong></span>
              <span style={{ fontSize: '12px', fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>Caddy: <strong>{currentCaddy}</strong></span>
            </div>
          </div>
          {versionsLoading ? <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}><Skeleton height={40} /><Skeleton height={40} /></div> : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="checkbox" id="update_web_app" checked={watchUpdateWebApp} onChange={e => form.setValue('update_web_app', e.target.checked)} style={{ accentColor: 'var(--brand)' }} />
                  <label htmlFor="update_web_app" style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', cursor: 'pointer' }}>Update Web App</label>
                </div>
                {watchUpdateWebApp && <select className="input-field" {...form.register('web_app_version')} style={selectStyle}>{versions?.webApp?.map(v => <option key={v} value={v}>{v}{v === tenant.web_app_version ? ' (current)' : ''}</option>)}</select>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="checkbox" id="update_openclaw" checked={watchUpdateOpenclaw} onChange={e => form.setValue('update_openclaw', e.target.checked)} style={{ accentColor: 'var(--brand)' }} />
                  <label htmlFor="update_openclaw" style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', cursor: 'pointer' }}>Update OpenClaw</label>
                </div>
                {watchUpdateOpenclaw && <select className="input-field" {...form.register('openclaw_version')} style={selectStyle}>{versions?.openclaw?.map(v => <option key={v} value={v}>{v}{v === tenant.openclaw_version ? ' (current)' : ''}</option>)}</select>}
              </div>
              {(pbUpgrade || caddyUpgrade) && (
                <div style={{ padding: '10px 14px', backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Also updating</span>
                  {pbUpgrade && <span style={{ fontSize: '12px', fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>PocketBase: {currentPb} → <strong>{pbUpgrade}</strong></span>}
                  {caddyUpgrade && <span style={{ fontSize: '12px', fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>Caddy: {currentCaddy} → <strong>{caddyUpgrade}</strong></span>}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {step === 'env_vars' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 16px', backgroundColor: 'var(--status-warning-dim)', border: '1px solid var(--status-warning)', borderRadius: 'var(--radius-md)' }}>
            <AlertTriangle size={16} style={{ color: 'var(--status-warning)', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>New environment variables detected</span>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>The selected version requires environment variables not present in the current configuration. These will be auto-resolved from global settings where possible.</p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {newEnvVars.map(varName => (
              <div key={varName} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: 'var(--bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                <Badge variant="warning">new</Badge>
                <span style={{ fontSize: '13px', fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{varName}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {step === 'progress' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <MilestoneProgress logs={logs} startedAt={provisionStartedAt} completedAt={provisionCompletedAt} milestoneDefs={UPDATE_MILESTONES} />
          <ProvisionLogViewer provisionId={provisionId} maxHeight="240px" />
          {isComplete && <div style={{ padding: '12px 16px', backgroundColor: 'var(--status-healthy-dim)', border: '1px solid var(--status-healthy)', borderRadius: 'var(--radius-md)', fontSize: '13px', color: 'var(--status-healthy)', display: 'flex', alignItems: 'center', gap: '8px' }}><RefreshCw size={16} /> Update completed successfully!</div>}
          {isFailed && <div style={{ padding: '12px 16px', backgroundColor: 'var(--status-unhealthy-dim)', border: '1px solid var(--status-unhealthy)', borderRadius: 'var(--radius-md)', fontSize: '13px', color: 'var(--status-unhealthy)' }}>Update failed. Check the logs above for details.</div>}
        </div>
      )}
    </Modal>
  )
}
